




/* 

    Not even gonna lie, not sure how the code ended up as "The functions that get the calculated indices are called GoButtons"
    Started off as I needed a button that would trigger the fetch call to get the data and it has evolved to this
    It would probably be good to have the API calls as separate functions and then attach a listener on the "Go Button" that calls those

    However, this is just one of those things that I don't consider a high enough priority to work on at this moment


*/




import { useState, useEffect } from "react";
import { useAppStateContext } from '../../hooks/useAppState';

const API_BASE = process.env.API_BASE ?? "";

// Go Button
export default function GoButton({
  datatype,
  onSuccess,
  onError,
  apiResource = 'index',
  disabled = false,
  className = "",
  children = "Go"
}) {
  const { bmpState, state } = useAppStateContext();
  const [loading, setLoading] = useState(false);

  // Extract values from context
  const { analysistype, bmptype, sitename, bmpname, selectedBmpType } = bmpState;
  const selectedAnalytes = state?.wq?.selectedAnalytes || [];

  // Build the request body based on analysistype and datatype
  function buildRequestBody(analysistype, datatype, sitename, bmpname, bmptype, selectedBmpType, selectedAnalytes) {
    let body = {};
    if (analysistype === "treatmenttrain") {
      body = { sitename };
    } else if (analysistype === "bmptype") {
      body = { bmptype };
    } else if (analysistype === "individual") {
      body = { sitename, bmpname };
      // Add selectedBmpType if available for individual BMP analysis
      if (selectedBmpType) {
        // snake case the selectedBmpType on the body just because I'm sending it to the API which runs python
        body.selected_bmp_type = selectedBmpType;
      } 
    }
    // If datatype is wq, add analytes array
    if (datatype === "wq") {
      body.analytes = selectedAnalytes;
    }
    return body;
  }

  async function handleClick() {
    setLoading(true);
    const url = `${API_BASE}/${datatype}/${apiResource}/${analysistype}`;
    const body = buildRequestBody(analysistype, datatype, sitename, bmpname, bmptype, selectedBmpType, selectedAnalytes);
    

    // Use the handleApiResponse helper from SelectionControls.jsx
    // We'll define a local copy here for now, but ideally this should be imported/shared.
    async function handleApiResponse(res) {
      const data = await res.json();
      if (!res.ok) {
        // Handle 422 errors specifically with user-friendly messages
        if (res.status === 422) {
          throw new Error(data.error || "No data found for the selected parameters. Please check your selections and try again.");
        }
        
        throw new Error(`HTTP ${res.status}: ${data.error || data.message || 'Unknown error'}`);
      }
      return data;
    }

    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body)
      });
      const data = await handleApiResponse(res);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      if (onError) onError(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <button
      type="button"
      className={`px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 ${className}`}
      onClick={handleClick}
      disabled={loading || disabled}
    >
      {loading ? "Loading..." : children}
    </button>
  );
}



// Mashup Go Button
export function MashupGoButton() {
  const { state, updateMpiData, updateMpiComparisonData, bmpState } = useAppStateContext();
  const mpiData = state.mpiData;
  const comparisonMode = state.mpiComparisonMode;
  const analysistype = bmpState.analysistype;
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const API_BASE = process.env.API_BASE || "https://api.sccwrp.org";

  // Recalculate comparison data when comparison mode or mpiData changes
  useEffect(() => {
    calculateComparisonMpiData();
  }, [comparisonMode, mpiData, state.wq.indexData, state.hydrology.indexData]);

  // Clear comparison data after fade transition completes when comparison mode is turned off
  useEffect(() => {
    if (!comparisonMode && state.mpiComparisonData.length > 0) {
      const timer = setTimeout(() => {
        updateMpiComparisonData([]);
      }, 500); // Match the fade transition timeout - this is mainly so they don't awkwardly see plots disappear suddenly
      
      return () => clearTimeout(timer);
    }
  }, [comparisonMode, state.mpiComparisonData.length, updateMpiComparisonData]);

  // Function to calculate comparison MPI data
  const calculateComparisonMpiData = () => {
    if (!comparisonMode || analysistype !== 'individual') {
      // Don't clear the data immediately - let the fade transition complete first
      return;
    }

    try {
      const comparisonMpiData = [];
      
      // Add WQ comparison data if available
      Object.entries(state.wq.indexData).forEach(([analyteName, analyteData]) => {
          const comparisonSummary = analyteData.index_summary?.find(d => d.bmp?.startsWith('Other'));
          if (comparisonSummary) {
              const existingEntry = mpiData.find(d => d.analytename === analyteName);
              if (existingEntry) {
                  comparisonMpiData.push({
                      ...existingEntry,
                      score: comparisonSummary.performance_index,
                      weight: existingEntry.weight // Use the same weights as main data
                    });
                }
            }
      });

      // Add Hydrology comparison data if available
      const hydrologyComparisonSummary = state.hydrology.indexData?.index_summary?.find(d => d.bmp?.startsWith('Other'));
      if (hydrologyComparisonSummary) {
        const hydrologyEntry = mpiData.find(d => d.analytename === "Hydrology");
        if (hydrologyEntry) {
          comparisonMpiData.push({
            ...hydrologyEntry,
            score: hydrologyComparisonSummary.performance_index,
            weight: hydrologyEntry.weight // Use the same weights as main data
          });
        }
      }

      updateMpiComparisonData(comparisonMpiData);
    } catch (comparisonError) {
      updateMpiComparisonData([]);
    }
  };

  // Only for within the scope of this function
  const handleClick = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      // Fetch main MPI data
      const res = await fetch(`${API_BASE}/mashup-index`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analytes: mpiData })
      });
      if (!res.ok) {
        const errData = await res.json();
        
        // Handle 422 errors specifically with user-friendly messages
        if (res.status === 422) {
          throw new Error(errData.error || "No data found for the selected parameters. Please check your selections and try again.");
        }
        
        throw new Error(errData.error || errData.message || 'Unknown error');
      }
      const data = await res.json();
      setResult(data);
      if (data.data && data.data.mashup_index_data && typeof updateMpiData === 'function') {
        const updatedMpiData = mpiData.map(entry => {
          const found = data.data.mashup_index_data.find(w => w.analytename === entry.analytename);
          return found ? { ...entry, weight: found.weight } : entry;
        });
        updateMpiData(updatedMpiData);
      }

      // Calculate comparison data
      calculateComparisonMpiData();
      
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6">
      <button
        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
        onClick={handleClick}
        disabled={loading || mpiData.length === 0}
      >
        {loading ? "Loading..." : "Go"}
      </button>
      
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded text-red-800 text-sm">
          <strong>Error:</strong> {error}
        </div>
      )}
    </div>
  );
}
